import { useState } from 'react';

import { IconAngleLeft } from '@iwoca/icons';
import { Button, Text } from '@iwoca/orion';
import { NEW_IwLink } from '@iwRouter/NEW_IwLink';
import { includes } from 'lodash';
import { FormattedMessage } from 'react-intl';

import { AddPhoneNumberForm } from './AddPhoneNumberForm';
import { SelectPhoneNumberField } from './SelectPhoneNumberField';
import { useAddPhoneNumber } from './useAddPhoneNumber';
import { useGetPeopleToVerify } from './useGetPeopleToVerify';
import { useSendVerificationSMS } from './useSendVerificationSMS';
import { TDeserialisedPeople } from '../../../modals/idCheckVerificationModal/IdCheckVerificationModal.types';
import { ONBOARDING_PATHS } from '../../constants';

export const CompleteIDCheckForm = () => {
    const { idCheckResponse } = useGetPeopleToVerify();
    const { sendVerificationSMS } = useSendVerificationSMS();
    const { addPhoneNumber, isLoading: isAddingPhone } = useAddPhoneNumber();
    const [addPhoneNumberFor, setAddPhoneNumberFor] = useState<string | null>(
        null
    );
    const [selectedPhones, setSelectedPhones] = useState<
        Record<string, string> | undefined
    >(undefined);
    const [sendingForPerson, setSendingForPerson] = useState<string | null>(
        null
    );
    const [successForPerson, setSuccessForPerson] = useState<string | null>(
        null
    );

    const toggleAddPhoneNumber = (personUid: string) => {
        setAddPhoneNumberFor(
            addPhoneNumberFor === personUid ? null : personUid
        );
    };

    const handleAddPhoneNumber = async (
        person: TDeserialisedPeople[number],
        phoneNumber: string
    ) => {
        try {
            await addPhoneNumber({ person, phoneNumber });
            setAddPhoneNumberFor(null);
            setSelectedPhones({
                ...selectedPhones,
                [person.uid]: phoneNumber
            });
        } catch (err) {
            console.error('Failed to add phone number:', err);
        }
    };

    const handleSubmit = (personUid: string) => async (e: React.FormEvent) => {
        e.preventDefault();
        const phoneNumber = selectedPhones?.[personUid];

        if (!phoneNumber) {
            return;
        }

        const person = idCheckResponse?.peopleToVerify.find(
            (p) => p.uid === personUid
        );

        if (!person) {
            return;
        }

        try {
            setSendingForPerson(personUid);
            await sendVerificationSMS({ person, phoneNumber });
            // Success - show success state
            setSuccessForPerson(personUid);
            setSendingForPerson(null);
        } catch (err) {
            // Error handling is done in the hook
            console.error('Failed to send verification SMS:', err);
            setSendingForPerson(null);
        }
    };

    const renderInfoMessage = (person: TDeserialisedPeople[number]) => {
        const hasSMSFailed = person.idCheckStatus === 'SMS failed';

        if (hasSMSFailed) {
            return (
                <Text className="text-xs font-reg">
                    <FormattedMessage
                        id="_.credit_card.onboarding.id_check_verification.sms_failed"
                        defaultMessage="Your SMS didn't go through. Please try it again by clicking 'Send SMS'."
                    />
                </Text>
            );
        }

        if (person.idCheckStatus === 'Something went wrong' && !hasSMSFailed) {
            return (
                <Text className="text-xs font-reg">
                    <FormattedMessage
                        id="_.credit_card.onboarding.id_check_verification.something_went_wrong"
                        defaultMessage="Something went wrong - please send another SMS and try again"
                    />
                </Text>
            );
        }

        if (person.idCheckStatus === 'Not started yet') {
            return (
                <Text className="text-xs font-reg">
                    <FormattedMessage
                        id="_.credit_card.onboarding.id_check.check_status"
                        defaultMessage="Check not started yet"
                    />
                </Text>
            );
        }

        if (includes(person.idCheckStatus, 'Verifying')) {
            return (
                <Text className="text-xs font-reg">
                    <FormattedMessage
                        id="_.credit_card.onboarding.id_check.verifying_check"
                        defaultMessage="Verifying your check"
                    />
                </Text>
            );
        }
    };

    return (
        <div className="my-l">
            <NEW_IwLink to={{ pathname: ONBOARDING_PATHS.ONBOARDING_CHECKS }}>
                <Text className="mb-xl flex flex-row gap-xs text-s font-bold text-primary-40">
                    <IconAngleLeft className="my-auto" />
                    <FormattedMessage
                        id="_.credit_card.onboarding.id_check.back_link"
                        defaultMessage="Back to list"
                    />
                </Text>
            </NEW_IwLink>

            <Text className="text-3xl font-bold">
                <FormattedMessage
                    id="_.credit_card.onboarding.id_check.title"
                    defaultMessage="ID Check"
                />
            </Text>

            <Text className="my-m text-xl font-reg">
                <FormattedMessage
                    id="_.credit_card.onboarding.id_check.description"
                    defaultMessage="Just to check you are who you say you are."
                />
            </Text>

            <Text>
                <FormattedMessage
                    id="_.credit_card.onboarding.id_check.instructions"
                    defaultMessage=" We use {id_verification_provider_name} verification, which is a secure way to do this. We’ll need you to take a photo of <strong>one</strong> of the following documents:"
                    values={{
                        strong: (chunks) => <strong>{chunks}</strong>,
                        id_verification_provider_name:
                            idCheckResponse?.id_verification_provider_name
                    }}
                />
            </Text>
            <div className="m-xl">
                <ul>
                    <li className="list-disc">
                        <Text>
                            <FormattedMessage
                                id="_.credit_card.onboarding.id_check.passport"
                                defaultMessage="Passport"
                            />
                        </Text>
                    </li>
                    <li className="list-disc">
                        <Text>
                            <FormattedMessage
                                id="_.credit_card.onboarding.id_check.driving_licence"
                                defaultMessage="Driving Licence"
                            />
                        </Text>
                    </li>
                </ul>
            </div>

            <Text>
                <FormattedMessage
                    id="_.credit_card.onboarding.id_check.phone_description"
                    defaultMessage="You’ll need to do this on your phone. Just pop your phone number below, and we’ll text you a link."
                />
            </Text>
            <div className="my-2xl">
                <Text className="my-l text-s font-med">
                    <FormattedMessage
                        id="_.credit_card.onboarding.id_check.mobile_number"
                        defaultMessage="Select a mobile number"
                    />
                </Text>

                {idCheckResponse!.peopleToVerify.map((person) => (
                    <form key={person.uid} onSubmit={handleSubmit(person.uid)}>
                        <div className="my-xl grid grid-cols-4 gap-l">
                            <div className="col-span-3 my-auto grid grid-cols-subgrid">
                                <div className="flex flex-col gap-xs">
                                    <label
                                        htmlFor={`${person.uid}`}
                                        className="font-s col-span-1 my-auto font-med"
                                    >
                                        {/* eslint-disable-next-line
                                        formatjs/no-literal-string-in-jsx */}
                                        {`${person.firstName} ${person.lastName}`}
                                    </label>

                                    {!addPhoneNumberFor ||
                                    addPhoneNumberFor !== person.uid
                                        ? renderInfoMessage(person)
                                        : null}
                                </div>
                                {addPhoneNumberFor === person.uid ? (
                                    <AddPhoneNumberForm
                                        onCancel={() =>
                                            toggleAddPhoneNumber(person.uid)
                                        }
                                        onSave={(phoneNumber) =>
                                            handleAddPhoneNumber(
                                                person,
                                                phoneNumber
                                            )
                                        }
                                        isLoading={isAddingPhone}
                                    />
                                ) : (
                                    <SelectPhoneNumberField
                                        name={`${person.uid}`}
                                        options={person.phones}
                                        disabled={false}
                                        value={
                                            selectedPhones
                                                ? selectedPhones[person.uid]
                                                : ''
                                        }
                                        onChange={(value) =>
                                            setSelectedPhones({
                                                ...selectedPhones,
                                                [person.uid]: value
                                            })
                                        }
                                        addNewNumber={() =>
                                            toggleAddPhoneNumber(person.uid)
                                        }
                                    />
                                )}
                            </div>

                            {addPhoneNumberFor !== person.uid && (
                                <Button
                                    type="submit"
                                    disabled={
                                        !selectedPhones ||
                                        !selectedPhones[person.uid] ||
                                        sendingForPerson === person.uid ||
                                        successForPerson === person.uid
                                    }
                                    className={`${successForPerson && 'bg-success-mid'},mt-m col-span-1 my-auto`}
                                >
                                    {successForPerson === person.uid ? (
                                        <FormattedMessage
                                            id="_.credit_card.onboarding.id_check.sms_sent"
                                            defaultMessage="SMS sent successfully!"
                                        />
                                    ) : sendingForPerson === person.uid ? (
                                        <FormattedMessage
                                            id="_.credit_card.onboarding.id_check.sending_sms"
                                            defaultMessage="Sending..."
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="_.credit_card.onboarding.id_check.send_sms"
                                            defaultMessage="Send SMS"
                                        />
                                    )}
                                </Button>
                            )}
                        </div>
                    </form>
                ))}
            </div>
        </div>
    );
};
